import { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';

import {
  PageLayoutContent,
  PageLayoutLeftPart,
  PageLayoutRightPart,
  PageLayoutTopPart,
} from '@/components/PageLayout';
import Button from '@/design_system/Button';
import Drawer, { DrawerBody } from '@/design_system/Drawer/Drawer';
import { Separator } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import IconFile from '@/icons/File.svg';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useMultiStepCreationContext } from '@/routes/Requests/contexts/MultiStepCreationContext';
import { ArticlePrice } from '@/routes/Requests/Request/Article/ArticlePrice';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { NewArticleBodyFormInformation } from './NewArticleBodyFormInformation';
import { NewArticleBodyFormInitialConditions } from './NewArticleBodyFormInitialConditions';
import { NewArticleBodyFormServices } from './NewArticleBodyFormServices';
import { NewArticleInfoSidePanelBody, NewArticleInfoSidePanelTop } from './NewArticleInfoSidePanel';
import { useNewArticleInfoSidePanelSections } from './useNewArticleInfoSidePanelSections';

import './NewArticleBody.css';

const { block } = createBEMClasses('new-article-body');

export const NewArticleBody = ({
  alertRef,
}: {
  alertRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { isMobile } = useViewPort();
  const { view } = useArticleContext();
  const { currentStep, currentStepIndex } = useMultiStepCreationContext();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { showSidePanel, showPrices, showProductName } = useNewArticleInfoSidePanelSections();

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    alertRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [alertRef, currentStepIndex]);

  return (
    <PageLayoutContent>
      <DrawerBody className={block()}>
        <PageLayoutLeftPart>
          {isMobile && showSidePanel && (
            <PageLayoutTopPart ref={ref}>
              {showPrices && <ArticlePrice allowDiscount={view.prices.discount.client.shown} />}
              {showSidePanel && (
                <>
                  <Button
                    style={{ flex: 1 }}
                    variant="secondary"
                    size="medium"
                    onPress={() => {
                      setIsSidePanelOpen(true);
                    }}
                  >
                    <IconFile />
                    <Trans id="requests.new.articles.drawer.open-button.title">
                      View item information
                    </Trans>
                  </Button>
                  <Drawer
                    isOpen={isSidePanelOpen}
                    onOpenChange={(close) => setIsSidePanelOpen(close)}
                    style={{ width: '320px' }}
                  >
                    <DrawerBody>
                      {showProductName && (
                        <>
                          <NewArticleInfoSidePanelTop />
                          <Separator />
                        </>
                      )}
                      <NewArticleInfoSidePanelBody />
                    </DrawerBody>
                  </Drawer>
                </>
              )}
            </PageLayoutTopPart>
          )}

          {currentStep.id === 'information' && (
            <NewArticleBodyFormInformation alertRef={alertRef} />
          )}

          {currentStep.id === 'initial-conditions' && (
            <NewArticleBodyFormInitialConditions alertRef={alertRef} />
          )}

          {currentStep.id === 'services' && <NewArticleBodyFormServices alertRef={alertRef} />}
        </PageLayoutLeftPart>

        {!isMobile && showSidePanel && (
          <PageLayoutRightPart style={{ padding: 0 }}>
            {(showProductName || showPrices) && (
              <>
                <Stack style={{ padding: '1rem 1.5rem' }}>
                  <NewArticleInfoSidePanelTop />
                </Stack>
                <Separator />
              </>
            )}
            <Stack style={{ padding: '0.5rem 1.5rem 1rem' }}>
              <NewArticleInfoSidePanelBody />
            </Stack>
          </PageLayoutRightPart>
        )}
      </DrawerBody>
    </PageLayoutContent>
  );
};
